import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Calendar, Input, MultiSelect, Select } from '@baseV2'
import { CountryCodes, enquiryPlatform, INSPECTION_USER_DESIGNATIONS, ORG_INDUSTRIES, LEADS_SOURCE, ORG_ROLES, REGION_TYPES, strings } from '@constants'
import { FormHeading } from '../../OrgsAndUsers/style'
import { Devices, Spacings } from '@styles'
import { createLead, getAllCommodityApi, getPanByCompanyName } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { getNumberWithCountryCode } from '@utils'
import { toggleDrawer } from '@data/state/action/root'
import { updateLead } from '@data/conn/put/leads'
import { BiFieldWrapper } from '../../OrgsAndUsers/Organisation/styles'
import OrgRole from '@data/model/NewModals/OrgRole'
import { isSalesAdmin } from 'src/utils/adminFilter'
import { RequiredMark } from '../styles'

const SectionItemWrapper = styled.div`
	flex-shrink: unset;
	flex-basis: 100%;
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};

	/* $flush marks the first section in the drawer: no heading above it, so no top margin either. */
	margin-top: ${({ $flush }) => ($flush ? 0 : Spacings.SPACING_2B)};
	& > div {
		width: 100%;
	}
`
const InputWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	/* Grid item: without this, a wide child (e.g. the MultiSelect chip row) stretches the column past the form. */
	min-width: 0;
	flex-direction: column;
	@media ${Devices.tablet} {
		flex-direction: row;
	}
	& > div {
		width: 100%;
	}
`
export const BiInputWrapper = styled.div`
	display: grid;
	grid-template-columns: ${({ isPhone }) => (isPhone ? `minmax(4.5rem, 1fr) 6fr` : `3fr 3fr`)};
	column-gap: ${Spacings.SPACING_2B};
	align-items: baseline;
	${({ IsPositionLeft }) => {
		if (IsPositionLeft) {
			return `grid-template-columns: 6fr 0fr;
					align-items: center;
					display: flex;
					flex-direction: column;
					div, button {
					width:100%;
					}
					@media ${Devices.mobile} {
						display: grid;
					}
				`
		}
	}}
`

const OrgNameWrapper = styled(BiFieldWrapper)`
	margin-bottom: 0;
	align-items: flex-end;
`

const SearchButton = styled(Button)`
	min-width: ${Spacings.SPACING_20B};
	height: ${Spacings.SPACING_10B};
`

const PhoneWrapper = styled(BiInputWrapper)`
	grid-template-columns: 1fr 2fr;
`

// Code | Phone Number | User Name share one row; on mobile they stack like every other row.
const PhoneNameWrapper = styled(BiInputWrapper)`
	grid-template-columns: 1fr;

	@media ${Devices.tablet} {
		grid-template-columns: 1fr 2fr 3fr;
	}
`

const withAsterisk = (label, required) =>
	required ? (
		<>
			{label}
			<RequiredMark>*</RequiredMark>
		</>
	) : (
		label
	)

const SOURCE = Object.entries(LEADS_SOURCE).map(([key, value]) => ({ key, label: value }))
const BUSINESS_CATEGORY = Object.entries(enquiryPlatform).map(([k, value]) => ({ key: value.name, label: value.name }))
const COMPANY_INDUSTRY = Object.entries(ORG_INDUSTRIES).map(([key, value]) => ({ key, label: value }))
const regionTypes = Object.entries(REGION_TYPES).map(([key, value]) => ({ key, label: value, disabled: key === REGION_TYPES.RAJASTHAN }))
const availableRoles = Object.values(ORG_ROLES)
	.filter(Boolean)
	.map((r) => {
		const isDefaultApproved = r.role === 'BUYER' && r.platform === 'MARKETPLACE'
		return {
			...new OrgRole(r).toJS(),
			requested: true,
			display_name: r.name,
			...(isDefaultApproved && { approved: true, default_role: true, requested: false }),
		}
	})

const USER_SUB_ROLE_OPTIONS = Object.entries(INSPECTION_USER_DESIGNATIONS).map(([key, label]) => ({ key, label }))

const deriveState = (data) => {
	const _state = {
		user_name: {
			id: 'user_name',
			placeholder: strings('user', 'name'),
			value: '',
			label: strings('user', 'name'),
			required: true,
		},
		email: {
			id: 'email',
			placeholder: strings('user', 'email'),
			value: '',
			label: strings('user', 'email'),
			required: false,
		},
		user_sub_role: {
			id: 'user_sub_role',
			placeholder: strings(['select', 'user', 'role']),
			value: '',
			label: strings('user', 'role'),
			data: USER_SUB_ROLE_OPTIONS,
			displayKey: 'label',
			primaryKey: 'key',
			required: false,
		},
		country_code: {
			id: 'org_country_code',
			value: CountryCodes[0],
			required: true,
		},
		mobile_number: {
			id: 'mobile_number',
			placeholder: strings('phone', 'number'),
			value: '',
			label: strings('phone', 'number'),
			required: true,
			valid: true,
		},
		pan: {
			id: 'pan',
			placeholder: strings(['enter', 'pan', 'number']),
			value: '',
			label: strings('pan', 'number'),
			required: true,
			valid: null,
		},
		organisation_name: {
			id: 'organisation_name',
			placeholder: strings(['enter', 'organisation', 'name']),
			value: '',
			label: strings('organisation', 'name'),
			required: true,
		},
		closure_date: {
			id: 'closure_date',
			placeholder: strings(['enter', 'closure', 'date']),
			value: new Date().getTime(),
			label: strings('closure', 'date'),
			required: true,
		},
		source: {
			id: 'source',
			placeholder: strings(['select', 'source']),
			primaryKey: 'key',
			displayKey: 'label',
			data: SOURCE?.filter((item) => item.key !== 'WEBSITE'),
			value: {},
			label: strings('source'),
			required: false,
		},
		category: {
			id: 'business_category',
			placeholder: strings(['select', 'business', 'category']),
			primaryKey: 'key',
			displayKey: 'label',
			data: BUSINESS_CATEGORY,
			value: {},
			label: strings('business', 'category'),
			required: true,
		},
		industry: {
			id: 'industry',
			placeholder: strings(['select', 'company_industry']),
			primaryKey: 'key',
			displayKey: 'label',
			data: COMPANY_INDUSTRY,
			value: {},
			label: strings('company_industry'),
			required: true,
		},
		region: {
			id: 'region',
			label: strings('region'),
			placeholder: strings('region'),
			primaryKey: 'key',
			displayKey: 'label',
			data: regionTypes,
			value: {},
			required: false,
		},
		roles: {
			id: 'roles',
			placeholder: strings(['select', 'roles']),
			primaryKey: 'display_name',
			displayKey: 'display_name',
			data: availableRoles,
			value: [],
			label: strings('roles'),
			required: true,
		},
		commodities: {
			id: 'commodities',
			placeholder: strings(['select', 'commodities']),
			primaryKey: 'commodity_id',
			displayKey: 'commodity_name',
			label: strings('commodities'),
			required: false,
			value: [],
		},
		location: {
			id: 'location',
			placeholder: strings(['enter', 'location']),
			value: '',
			label: strings('location'),
			required: true,
		},
		turnover: {
			id: 'turnover',
			placeholder: strings(['enter', 'turnover']),
			value: '',
			label: strings('turnover_in_cr'),
			required: false,
		},
	}
	if (data) {
		_state.email.value = data?.user_email
		_state.user_name.value = data?.user_details?.full_name
		_state.country_code.value = CountryCodes.find((item) => item.code === data.country_code) || CountryCodes[0]
		_state.mobile_number.value = data?.user_details?.mobile_number
		_state.pan.value = data?.pan
		_state.organisation_name.value = data?.user_details?.organisation_name
		_state.location.value = data?.location
		_state.closure_date.value = data?.expected_closure_date
		_state.turnover.value = data?.turnover
		_state.source.value = SOURCE.find((item) => item.key === data?.source) || {}
		_state.category.value = BUSINESS_CATEGORY.find((item) => item.key === data?.category) || {}
		_state.industry.value = COMPANY_INDUSTRY.find((item) => item.key === data?.industry) || {}
		_state.region.value = regionTypes.find((item) => item.key === data?.region) || {}
		if (data?.region === REGION_TYPES.EXPORT) {
			_state.pan.required = false
		}
	}
	return _state
}

// Region / business category (platform) defaults taken from the logged-in SALES_ADMIN's own profile.
const getAdminDefaults = (user) => {
	const region = regionTypes.find((item) => item.key === user?.region && !item.disabled)
	const category = BUSINESS_CATEGORY.find((item) => (user?.platforms || []).includes(item.key))
	return { region, category }
}

const AddLead = ({ update, data, isEdit = false }) => {
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const isSalesUser = isSalesAdmin(loggedInUser?.role, false)
	// Only a SALES_ADMIN creating a new lead gets region / commodities / category pre-selected (and locked).
	const prefillDefaults = !isEdit && !data && isSalesUser
	const [adminDefaults] = useState(() => (prefillDefaults ? getAdminDefaults(loggedInUser) : {}))

	const [formState, setFormState] = useState(() => {
		const _state = deriveState(data)
		if (!prefillDefaults) return _state
		const { region, category } = adminDefaults
		if (region) {
			_state.region.value = region
			_state.pan.required = region.key !== REGION_TYPES.EXPORT
		}
		if (category) _state.category.value = category
		return _state
	})
	const dispatch = useDispatch()
	const [commodityList, setCommodityList] = useState([])
	const [lead, setLead] = useState(() => {
		if (!prefillDefaults) return {}
		const { region, category } = adminDefaults
		return { ...(region && { region: region.key }), ...(category && { category: category.key }) }
	})
	const userId = useSelector((state) => state.getIn(['user', 'user', 'user_id']))
	const [panByCompanyName, setPanByCompanyName] = useState([])
	const [_orgName, setOrgName] = useState()

	const isLogistics = useMemo(() => (formState.roles?.value || []).some((r) => r?.platform === 'LOGISTICS'), [formState.roles?.value])

	useEffect(() => {
		dispatch(showLoader(true))
		getAllCommodityApi()
			.then((response) => {
				setCommodityList(response)
				if (prefillDefaults) {
					// Reuse the list's own objects so the MultiSelect primaryKey matches.
					const userCommodities = loggedInUser?.commodity || []
					const selected = (response || []).filter((c) =>
						userCommodities.some((uc) => uc?.commodity_id === c.commodity_id || uc?.commodity_name === c.commodity_name)
					)
					if (selected.length) {
						setFormState((state) => ({ ...state, commodities: { ...state.commodities, value: selected } }))
						setLead((l) => ({ ...l, commodities: selected }))
					}
				}
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [])

	const updateState = (field, key) => (value) => {
		setFormState((state) => ({
			...state,
			[field]: {
				...state[field],
				[key]: value,
			},
		}))

		if (isEdit) {
			if (field === 'user_name') {
				setLead({
					...lead,
					['user_details']: {
						['full_name']: value,
					},
				})
			} else if (field === 'organisation_name') {
				setLead({
					...lead,
					['user_details']: {
						[field]: value,
					},
				})
			} else if (field === 'source' || field === 'category' || field === 'industry' || field === 'region' || field === 'user_sub_role') {
				if (field === 'region') {
					// Only toggle the required flag; keep the PAN/Registration value — the label switches
					// via the region (render-time), so the user's entered value must NOT be wiped.
					updateState('pan', 'required')(value.key !== REGION_TYPES.EXPORT)
				}
				setLead({ ...lead, [field]: value.key })
			} else if (field === 'closure_date') {
				setLead({ ...lead, ['expected_closure_date']: value })
			} else if (field === 'email') {
				setLead({ ...lead, ['user_email']: value })
			} else {
				setLead({ ...lead, [field]: value })
			}
		} else {
			if (field === 'country_code' && adminDefaults.region) {
				// Region is locked for SALES_ADMIN — a country change must not override it; PAN follows the locked region.
				const isExport = adminDefaults.region.key === REGION_TYPES.EXPORT
				setFormState((state) => ({
					...state,
					pan: {
						...state.pan,
						required: value?.code === 'IN' && !isExport,
						label: strings('pan', 'number'),
						placeholder: strings(['enter', 'pan', 'number']),
						value: '',
					},
				}))
				setLead({ ...lead, [field]: value?.code })
			} else if (field === 'country_code') {
				if (value?.code !== 'IN') {
					setFormState((state) => ({
						...state,
						region: { ...state.region, value: { key: REGION_TYPES.EXPORT, label: REGION_TYPES.EXPORT } },
						pan: { ...state.pan, required: false, value: '' },
					}))
					setLead({ ...lead, country_code: value?.code, region: REGION_TYPES.EXPORT })
				} else {
					setFormState((state) => ({
						...state,
						region: { ...state.region, value: {} },
						pan: {
							...state.pan,
							required: true,
							label: strings('pan', 'number'),
							placeholder: strings(['enter', 'pan', 'number']),
							value: '',
						},
					}))
					setLead({ ...lead, [field]: value?.code, region: undefined })
				}
			} else if (field === 'source' || field === 'category' || field === 'industry' || field === 'region' || field === 'user_sub_role') {
				if (field === 'region') {
					// Only toggle the required flag; keep the PAN/Registration value (don't wipe user input).
					updateState('pan', 'required')(value.key !== REGION_TYPES.EXPORT)
				}
				setLead({ ...lead, [field]: value.key })
			} else {
				setLead({ ...lead, [field]: value })
			}
		}
	}

	const orgNameHander = () => {
		const orgName = formState?.organisation_name?.value?.trim()
		if (orgName === _orgName || orgName === '') {
			return
		}
		dispatch(showLoader(true))
		getPanByCompanyName(orgName?.trim())
			.then((res) => {
				const _res =
					res?.length > 0
						? res?.map((item) => {
								item['display_name'] = `${item.pan + ', (' + item.company_name + '), ' + item.location + ''}`
								return item
							})
						: []
				setPanByCompanyName(_res)
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, err?.message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				setOrgName(orgName)
				dispatch(showLoader(false))
			})
	}

	const panValidation = () => {
		let regpan = /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/
		const panValue = typeof formState.pan?.value === 'object' ? formState.pan?.value?.pan : formState.pan?.value
		return regpan.test(panValue)
	}

	const emailValidation = () => {
		const emailRegex =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		return emailRegex.test(formState.email?.value)
	}

	const addLeadHandler = () => {
		if (!formState.roles?.value?.length) {
			dispatch(showToast(true, strings('msg_select_at_least_one_role'), { type: TOAST_TYPE.ERROR }))
			return
		}
		if (formState.email?.value && !emailValidation()) {
			dispatch(showToast(true, strings('msg_invalid_email'), { type: TOAST_TYPE.ERROR }))
			return
		}
		if (formState.pan?.value && formState.region?.value?.key !== REGION_TYPES.EXPORT && !panValidation()) {
			dispatch(showToast(true, strings('msg_invalid_pan'), { type: TOAST_TYPE.ERROR }))
			return
		}
		dispatch(showLoader(true))
		createLead(userId, {
			...lead,
			closure_date: formState.closure_date.value,
			country_code: formState.country_code?.value?.code,
			mobile_number: getNumberWithCountryCode(formState.country_code?.value, formState?.mobile_number.value),
			pan: typeof formState.pan?.value === 'object' ? formState.pan?.value?.pan : formState.pan?.value,
		})
			.then(() => {
				dispatch(showToast(true, strings('msg_lead_added_successfully'), { type: TOAST_TYPE.SUCCESS }))
				update()
				dispatch(toggleDrawer(false))
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, err?.response?.data?.message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const editLeadHandler = () => {
		if (!formState.roles?.value?.length) {
			dispatch(showToast(true, strings('msg_select_at_least_one_role'), { type: TOAST_TYPE.ERROR }))
			return
		}
		if (formState.email?.value && !emailValidation()) {
			dispatch(showToast(true, strings('msg_invalid_email'), { type: TOAST_TYPE.ERROR }))
			return
		}
		if (formState.pan?.value && formState.region?.value?.key !== REGION_TYPES.EXPORT && !panValidation()) {
			dispatch(showToast(true, strings('msg_invalid_pan'), { type: TOAST_TYPE.ERROR }))
			return
		}
		dispatch(showLoader(true))
		let userDetails = { ...data['user_details'], ...lead['user_details'] }
		let leadObj = {
			...data,
			...lead,
			user_details: userDetails,
			country_code: formState.country_code?.value?.code,
			mobile_number: getNumberWithCountryCode(formState.country_code?.value, formState?.mobile_number.value),
			pan: typeof formState.pan?.value === 'object' ? formState.pan?.value?.pan : formState.pan?.value,
		}
		updateLead(leadObj)
			.then((response) => {
				dispatch(showToast(true, strings('msg_lead_updated_successfully'), { type: TOAST_TYPE.SUCCESS }))
				update()
				dispatch(showLoader(false))
				dispatch(toggleDrawer(false))
			})
			.catch((err) => {
				console.log(err)
				dispatch(showToast(true, strings('msg_lead_update_error'), { type: TOAST_TYPE.ERROR }))
			})
	}

	const disabled = useMemo(() => {
		const relaxedByLogistics = ['category', 'location', 'pan']
		return (
			Object.entries(formState).some(([fieldKey, d]) => {
				const required = d.required && !(isLogistics && relaxedByLogistics.includes(fieldKey))
				return (
					required &&
					(Array.isArray(d.value) ? !d.value.length : d.value && typeof d.value === 'object' ? !Object.keys(d.value).length : !d.value)
				)
			}) ||
			!formState.mobile_number.valid ||
			(formState.email?.value && !emailValidation()) ||
			(formState.pan?.value && formState.region?.value?.key !== REGION_TYPES.EXPORT && !panValidation())
		)
	}, [formState, isLogistics])

	return (
		<>
			<DrawerBodyWrapper>
				{/* No section heading here — the drawer title already says what this is. */}
				<SectionItemWrapper $flush>
					<OrgNameWrapper IsPositionLeft>
						<Input
							id={formState.organisation_name.id}
							label={withAsterisk(formState.organisation_name.label, formState.organisation_name.required)}
							type='text'
							placeholder={formState.organisation_name.placeholder}
							value={formState.organisation_name.value}
							onChange={(e) => updateState('organisation_name', 'value')(e.target.value)}
							required={formState.organisation_name.required}
						/>
						<SearchButton onClick={() => orgNameHander()}>{strings('search')}</SearchButton>
					</OrgNameWrapper>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<InputWrapper>
						<PhoneNameWrapper>
							<Select
								id={formState.country_code.id}
								label={withAsterisk(strings('code'), formState.country_code.required)}
								data={CountryCodes}
								displayKey='dial_code'
								primaryKey='name'
								value={formState.country_code.value}
								onChange={updateState('country_code', 'value')}
								type='tel'
								noDropIcon
								disabled={isEdit}
							/>
							<Input
								id={formState.mobile_number.id}
								label={withAsterisk(formState.mobile_number.label, formState.mobile_number.required)}
								type='tel'
								placeholder={formState.mobile_number.placeholder}
								value={formState.mobile_number.value}
								pattern='^\d{7}\d+$'
								maxLength={10}
								onChange={(e) => {
									const valid = e.target.validity.valid
									updateState('mobile_number', 'value')(e.target.value)
									setFormState((_s) => ({
										..._s,
										mobile_number: {
											..._s['mobile_number'],
											valid,
										},
									}))
								}}
								required={formState.mobile_number.required}
								errorMap={{
									patternMismatch: `${strings('enter_valid_num')}`,
									valueMissing: `${strings('mob_num_required')}`,
								}}
								disabled={isEdit}
							/>
							<Input
								id={formState.user_name.id}
								label={withAsterisk(strings('user', 'name'), formState.user_name.required)}
								type='text'
								placeholder={strings('user', 'name')}
								value={formState.user_name.value}
								onChange={(e) => updateState('user_name', 'value')(e.target.value)}
								required={formState.user_name.required}
								errorMap={{
									valueMissing: `${strings('name_required')}`,
								}}
							/>
						</PhoneNameWrapper>
					</InputWrapper>
					<InputWrapper>
						<BiInputWrapper>
							<Input
								id={formState.email.id}
								label={formState.email.label}
								type='email'
								pattern='^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
								errorMap={{
									patternMismatch: `${strings('enter_valid_email')}`,
								}}
								placeholder={formState.email.placeholder}
								value={formState.email.value}
								onChange={(e) => updateState('email', 'value')(e.target.value)}
								required={formState.email.required}
							/>
							<Select
								id={formState.user_sub_role?.id}
								label={formState.user_sub_role?.label}
								data={formState.user_sub_role?.data}
								value={formState.user_sub_role?.value}
								onChange={updateState('user_sub_role', 'value')}
								placeholder={formState.user_sub_role?.placeholder}
								displayKey={formState.user_sub_role?.displayKey}
								primaryKey={formState.user_sub_role?.primaryKey}
							/>
						</BiInputWrapper>
					</InputWrapper>
					<InputWrapper>
						<BiInputWrapper>
							{formState.region?.value?.key === REGION_TYPES.EXPORT ? (
								<Input
									id={formState.pan.id}
									label={strings('registration')}
									type='text'
									placeholder={strings('enter', 'registration')}
									value={formState.pan.value}
									onChange={(e) => updateState('pan', 'value')(e.target.value)}
								/>
							) : panByCompanyName?.length > 0 ? (
								<Select
									id={formState.pan.id}
									label={withAsterisk(formState.pan.label, formState.pan.required && !isLogistics)}
									placeholder={strings(['select', 'pan', 'number'])}
									data={panByCompanyName}
									displayKey='display_name'
									primaryKey='pan'
									value={panByCompanyName?.find((item) => item?.pan === formState.pan?.value?.pan) ?? (formState.pan.value || {})}
									onChange={(val) => {
										updateState('pan', 'value')(val)
									}}
									createNewOption={true}
									maxLength='10'
									uppercase
									required={formState.pan.required && !isLogistics}
								/>
							) : (
								<Input
									id={formState.pan.id}
									label={withAsterisk(formState.pan.label, formState.pan.required && !isLogistics)}
									type='text'
									placeholder={formState.pan.placeholder}
									value={formState.pan.value}
									maxLength='10'
									uppercase
									pattern='[A-Z]{5}[0-9]{4}[A-Z]{1}'
									errorMap={{
										patternMismatch: strings('enter_valid_pan'),
									}}
									onChange={(e) => updateState('pan', 'value')(e.target.value.toUpperCase())}
									required={formState.pan.required && !isLogistics}
								/>
							)}
							<Calendar
								label={withAsterisk(formState.closure_date.label, formState.closure_date.required)}
								id={formState.closure_date.id}
								value={formState.closure_date.value}
								onChange={updateState('closure_date', 'value')}
							/>
						</BiInputWrapper>
					</InputWrapper>
					<InputWrapper>
						<BiInputWrapper>
							<Select
								label={formState.source.label}
								id={formState.source.id}
								placeholder={formState.source.placeholder}
								value={formState.source.value}
								onChange={updateState('source', 'value')}
								data={formState.source.data}
								displayKey={formState.source.displayKey}
								primaryKey={formState.source.primaryKey}
								openBelow={false}
								required={formState.source.required}
							/>
							<Input
								id={formState.location.id}
								label={withAsterisk(formState.location.label, formState.location.required && !isLogistics)}
								type='text'
								placeholder={formState.location.placeholder}
								value={formState.location.value}
								onChange={(e) => updateState('location', 'value')(e.target.value)}
								required={formState.location.required && !isLogistics}
							/>
						</BiInputWrapper>
					</InputWrapper>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<BiInputWrapper>
						<InputWrapper>
							<MultiSelect
								id={'select'}
								label={withAsterisk(strings('roles'), formState.roles.required)}
								placeholder={strings('select', 'roles')}
								data={formState.roles.data}
								value={formState.roles.value}
								displayKey={formState.roles.displayKey}
								primaryKey={formState.roles.primaryKey}
								onChange={updateState('roles', 'value')}
								required={!formState.roles.value?.length}
							/>
						</InputWrapper>
						<InputWrapper>
							<MultiSelect
								id={'select-commodity'}
								label={strings('commodities')}
								placeholder={strings('select', 'commodities')}
								data={commodityList}
								value={formState.commodities.value === '' ? [] : formState.commodities.value}
								displayKey={formState.commodities.displayKey}
								primaryKey={formState.commodities.primaryKey}
								onChange={updateState('commodities', 'value')}
							/>
						</InputWrapper>
					</BiInputWrapper>
				</SectionItemWrapper>
				<SectionItemWrapper>
					<FormHeading>{strings('organisation', 'details')}</FormHeading>
					<BiInputWrapper>
						<InputWrapper>
							<Select
								label={withAsterisk(formState.category.label, formState.category.required && !isLogistics)}
								id={formState.category.id}
								placeholder={formState.category.placeholder}
								value={formState.category.value}
								onChange={updateState('category', 'value')}
								data={formState.category.data}
								displayKey={formState.category.displayKey}
								primaryKey={formState.category.primaryKey}
								openBelow={false}
								required={formState.category.required && !isLogistics}
							/>
						</InputWrapper>
						<InputWrapper>
							<Select
								label={withAsterisk(formState.industry.label, formState.industry.required)}
								id={formState.industry.id}
								placeholder={formState.industry.placeholder}
								value={formState.industry.value}
								onChange={updateState('industry', 'value')}
								data={formState.industry.data}
								displayKey={formState.industry.displayKey}
								primaryKey={formState.industry.primaryKey}
								openBelow={false}
								required={formState.industry.required}
							/>
						</InputWrapper>
					</BiInputWrapper>
									<InputWrapper>
						<BiInputWrapper>
							<Input
								id={formState.turnover.id}
								label={formState.turnover.label}
								type='number'
								placeholder={formState.turnover.placeholder}
								value={formState.turnover.value}
								onChange={(e) => updateState('turnover', 'value')(e.target.value === '' ? '' : Math.abs(e.target.value))}
								required={formState.turnover.required}
							/>
							<Select
								id={formState.region?.id}
								label={formState.region?.label}
								data={formState.region?.data}
								value={formState.region?.value}
								onChange={updateState('region', 'value')}
								placeholder={formState?.region?.placeholder}
								displayKey={formState?.region?.displayKey}
								primaryKey={formState?.region?.primaryKey}
							/>
						</BiInputWrapper>
					</InputWrapper>
</SectionItemWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				{isEdit ? (
					<Button disabled={disabled} onClick={editLeadHandler}>
						{strings('edit', 'lead')}
					</Button>
				) : (
					<Button disabled={disabled} onClick={addLeadHandler}>
						{strings('add', 'lead')}
					</Button>
				)}
			</DrawerFooterWrapper>
		</>
	)
}

export default AddLead
